import dayjs from 'dayjs';

export const SIC_KPI_BOARD_ID = 8;

export const SIC_PASS_THRESHOLD = 0.9;

export const SIC_BOARD_TYPE = {
  balancedScorecard: 0,
  sic: 1
};

export const DEFAULT_WEEK_START_DAY = 1;

export const YES_NO_TARGET_TYPE_NAME = 'Yes/No';

export const DAILY_SCHEDULE_TYPE_NAME = 'Daily';

export const SIC_COMPARE_TYPE = {
  greaterThan: 0,
  lessThan: 1,
  greaterOrEqual: 2,
  lessOrEqual: 3,
  equal: 4,
  none: 5
};

export const SIC_COMPARE_TYPES = [
  { value: SIC_COMPARE_TYPE.greaterThan, symbol: '>', label: '>' },
  { value: SIC_COMPARE_TYPE.lessThan, symbol: '<', label: '<' },
  { value: SIC_COMPARE_TYPE.greaterOrEqual, symbol: '>=', label: '>=' },
  { value: SIC_COMPARE_TYPE.lessOrEqual, symbol: '<=', label: '<=' },
  { value: SIC_COMPARE_TYPE.equal, symbol: '=', label: '=' },
  { value: SIC_COMPARE_TYPE.none, symbol: '-', label: 'None (display only)' }
];

export const WEEK_START_DAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

export const SIC_API_DATE_FORMAT = 'YYYY-MM-DD';

const AVERAGED_UOMS = ['%'];

export const isBlankValue = value =>
  value === null || value === undefined || value === '';

export const toNumberOrNull = value => {
  if (isBlankValue(value)) return null;
  const number = Number(String(value).replace(/,/g, ''));
  return Number.isNaN(number) ? null : number;
};

export const compareTypeSymbol = compareType =>
  SIC_COMPARE_TYPES.find(option => option.value === compareType)?.symbol ??
  '-';

export const buildWeekDates = (selectedDate, weekStartDay) => {
  const selected = dayjs(selectedDate).startOf('day');
  const daysSinceWeekStart = (selected.day() - weekStartDay + 7) % 7;
  const weekStart = selected.subtract(daysSinceWeekStart, 'day');
  return Array.from({ length: 7 }, (_, dayIndex) =>
    weekStart.add(dayIndex, 'day')
  );
};

export const buildMonthStartsOfWeek = weekDates => [
  ...new Set(
    weekDates.map(date => date.startOf('month').format(SIC_API_DATE_FORMAT))
  )
];

export const isMeasurableCountedInSic = measurable =>
  measurable.affectsSic && measurable.compareType !== SIC_COMPARE_TYPE.none;

export const isTargetAchieved = (value, target, measurable) => {
  if (isBlankValue(value)) return null;
  if (measurable.isYesNo) return Number(value) === 1;

  const numericValue = toNumberOrNull(value);
  const numericTarget = toNumberOrNull(target);
  if (numericValue === null || numericTarget === null) return null;

  switch (measurable.compareType) {
    case SIC_COMPARE_TYPE.greaterThan:
      return numericValue > numericTarget;
    case SIC_COMPARE_TYPE.lessThan:
      return numericValue < numericTarget;
    case SIC_COMPARE_TYPE.greaterOrEqual:
      return numericValue >= numericTarget;
    case SIC_COMPARE_TYPE.lessOrEqual:
      return numericValue <= numericTarget;
    case SIC_COMPARE_TYPE.equal:
      return numericValue === numericTarget;
    default:
      return null;
  }
};

export const isMeasurableAchievedOnDay = (measurable, dayIndex) =>
  isMeasurableCountedInSic(measurable)
    ? isTargetAchieved(
        measurable.values[dayIndex],
        measurable.targets[dayIndex],
        measurable
      )
    : null;

const average = numbers =>
  numbers.length
    ? numbers.reduce((sum, number) => sum + number, 0) / numbers.length
    : null;

export const calculateSectorSicScore = (measurables, dayIndex) => {
  const results = measurables
    .map(measurable => isMeasurableAchievedOnDay(measurable, dayIndex))
    .filter(result => result !== null);

  if (!results.length) return null;
  return results.filter(Boolean).length / results.length;
};

export const calculateDailySicScore = (sectors, dayIndex) =>
  average(
    sectors
      .map(sector => calculateSectorSicScore(sector.measurables, dayIndex))
      .filter(score => score !== null)
  );

export const calculateWeeklyAverage = dailyScores =>
  average(dailyScores.filter(score => score !== null));

export const calculateMeasurableWeekTotal = measurable => {
  const enteredValues = measurable.values.filter(value => !isBlankValue(value));
  if (!enteredValues.length) return null;

  if (measurable.isYesNo) {
    const yesCount = enteredValues.filter(value => Number(value) === 1).length;
    return `${yesCount}/${enteredValues.length}`;
  }

  const numericValues = enteredValues
    .map(toNumberOrNull)
    .filter(value => value !== null);
  if (!numericValues.length) return null;

  const total = AVERAGED_UOMS.includes(measurable.displayUom)
    ? average(numericValues)
    : numericValues.reduce((sum, value) => sum + value, 0);
  return Math.round(total * 100) / 100;
};

export const formatSicPercent = score =>
  score === null || score === undefined ? '-' : `${Math.round(score * 100)}%`;

export const isSicScorePassing = score =>
  score !== null && score >= SIC_PASS_THRESHOLD;

export const formatTargetDisplay = measurable => {
  if (measurable.isYesNo) return 'Yes';
  const distinctTargets = [
    ...new Set(measurable.targets.filter(target => !isBlankValue(target)))
  ];
  if (!distinctTargets.length) return 'TBC';
  return distinctTargets.join(' / ');
};

export const buildSicSectors = ({
  sectors,
  measurables,
  targets,
  dataItems,
  weekDates,
  yesNoTargetTypeId
}) => {
  const targetByMeasurableAndMonth = new Map(
    targets.map(target => [
      `${target.measurableId}|${dayjs(target.date).format('YYYY-MM')}`,
      target.target
    ])
  );
  const valueByMeasurableAndDate = new Map(
    dataItems.map(item => [
      `${item.measurableId}|${dayjs(item.date).format(SIC_API_DATE_FORMAT)}`,
      item.value
    ])
  );

  return sectors.map(sector => ({
    ...sector,
    measurables: measurables
      .filter(measurable => measurable.sectorId === sector.id)
      .map(measurable => {
        const isYesNo =
          yesNoTargetTypeId !== null &&
          measurable.targetType === yesNoTargetTypeId;
        return {
          ...measurable,
          isYesNo,
          displayUom: isYesNo
            ? 'Yes/No'
            : measurable.uom?.trim() || measurable.targetTypeValue,
          targets: weekDates.map(date =>
            isYesNo
              ? 1
              : targetByMeasurableAndMonth.get(
                  `${measurable.id}|${date.format('YYYY-MM')}`
                ) ?? null
          ),
          values: weekDates.map(
            date =>
              valueByMeasurableAndDate.get(
                `${measurable.id}|${date.format(SIC_API_DATE_FORMAT)}`
              ) ?? null
          )
        };
      })
  }));
};
